import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type {
  PaidMediaOverviewRevenueContributionLeader,
  PaidMediaOverviewSummaryPlatform,
} from "src/services/PaidMediaOverviewAPI";
import { overviewPlatformLabel } from "../../utils/paidMediaOverviewLabels";
import { formatOverviewPct } from "../../utils/paidMediaOverviewFormat";
import { OVERVIEW_PLATFORM_DESCRIPTORS } from "../../data/paidMediaOverviewPlatforms";
import {
  OverviewMetricTip,
  OverviewShareBar,
  type OverviewShareBarVariant,
} from "./overviewPrimitives";

interface PlatformContributionCardProps {
  platforms: PaidMediaOverviewSummaryPlatform[];
  revenueContributionLeader: PaidMediaOverviewRevenueContributionLeader | null;
}

type ShareMetric = "spend_share" | "revenue_share" | "purchase_share";

const SHARE_ROWS: Array<{ key: string; metric: ShareMetric; titleKey: string; tooltipId: string }> = [
  {
    key: "spendShare",
    metric: "spend_share",
    titleKey: "paidMediaOverview.metrics.spendShare",
    tooltipId: "spendShare",
  },
  {
    key: "revenueShare",
    metric: "revenue_share",
    titleKey: "paidMediaOverview.metrics.revenueShare",
    tooltipId: "revenueShare",
  },
  {
    key: "purchaseShare",
    metric: "purchase_share",
    titleKey: "paidMediaOverview.metrics.purchaseShare",
    tooltipId: "purchaseShare",
  },
];

const PlatformContributionCard: FC<PlatformContributionCardProps> = ({
  platforms,
  revenueContributionLeader,
}) => {
  const { t } = useTranslation();

  const leaderLabel = overviewPlatformLabel(t, revenueContributionLeader?.platform);

  return (
    <article className="paid-media-panel paid-media-overview__contribution-card">
      <div className="paid-media-overview__panel-header">
        <div>
          <h3>{t("paidMediaOverview.contribution.title")}</h3>
          <p className="paid-media-overview__panel-subtitle">
            {t("paidMediaOverview.contribution.subtitle")}
          </p>
        </div>
      </div>
      <div className="paid-media-overview__contribution-rows">
        {SHARE_ROWS.map(row => {
          const title = t(row.titleKey);
          return (
            <div key={row.key} className="paid-media-overview__contribution-row">
              <div className="paid-media-overview__contribution-row-title">
                {title}
                <OverviewMetricTip id={row.tooltipId} label={title} />
              </div>
              {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
                <OverviewShareBar
                  key={descriptor.key}
                  label={t(descriptor.labelKey)}
                  value={
                    platforms?.find(entry => entry?.platform === descriptor.key)?.[row.metric] ??
                    null
                  }
                  variant={`${descriptor.slug}-ads` as OverviewShareBarVariant}
                />
              ))}
            </div>
          );
        })}
      </div>
      {leaderLabel ? (
        <div className="paid-media-overview__callout paid-media-overview__callout--success">
          <strong>{t("paidMediaOverview.contribution.insightLabel")}</strong>{" "}
          {t("paidMediaOverview.contribution.insightCopy", {
            platform: leaderLabel,
            revenueShare: formatOverviewPct(revenueContributionLeader?.revenue_share, 0),
            spendShare: formatOverviewPct(revenueContributionLeader?.spend_share, 0),
          })}
        </div>
      ) : null}
    </article>
  );
};

export default PlatformContributionCard;
